import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../common/AuthContext";

const SponsorshipTable = ({ onSelectedSponsorshipsChange, selectedIds }) => {
  const { myConferenceId } = useAuth();

  const [data, setData] = useState([]);
  const [selectedSponsorshipIds, setSelectedSponsorshipIds] = useState([]);

  const [conferences, setConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState("");
  const [loadingConferences, setLoadingConferences] = useState(false);

  const [selectFocused, setSelectFocused] = useState(false);

  useEffect(() => {
    setSelectedSponsorshipIds(Array.isArray(selectedIds) ? selectedIds : []);
  }, [selectedIds]);

  const handleCheckboxChange = (id) => {
    const valid = Array.isArray(selectedSponsorshipIds)
      ? selectedSponsorshipIds
      : [];

    const updated = valid.includes(id)
      ? valid.filter((prevId) => prevId !== id)
      : [...valid, id];

    setSelectedSponsorshipIds(updated);
    onSelectedSponsorshipsChange(updated);
  };

  const getConferences = useCallback(() => {
    setLoadingConferences(true);

    axios
      .get("https://eventscons.com/eventbackendnew/api/conferences/all")
      .then((res) => {
        const list = res?.data?.data?.data || res?.data?.data || res?.data || [];
        const conferenceList = Array.isArray(list) ? list : [];

        setConferences(conferenceList);

        if (conferenceList.length > 0) {
          const matchedConference = conferenceList.find((c) => {
            const id = c?.id || c?.conference_id || c?._id;
            return String(id) === String(myConferenceId);
          });

          if (matchedConference) {
            const matchedId =
              matchedConference?.id ||
              matchedConference?.conference_id ||
              matchedConference?._id;

            setSelectedConferenceId(String(matchedId));
          } else {
            const firstId =
              conferenceList[0]?.id ||
              conferenceList[0]?.conference_id ||
              conferenceList[0]?._id ||
              "";

            setSelectedConferenceId(String(firstId));
          }
        } else {
          setSelectedConferenceId("");
        }
      })
      .catch((err) => {
        console.error("Error fetching conferences:", err);
        setConferences([]);
        setSelectedConferenceId("");
      })
      .finally(() => setLoadingConferences(false));
  }, [myConferenceId]);

  useEffect(() => {
    getConferences();
  }, [getConferences]);

  const getData = useCallback(() => {
    if (!selectedConferenceId) {
      setData([]);
      return;
    }

    axios
      .get(
        `https://eventscons.com/eventbackendnew/api/sponsorshippackages/${selectedConferenceId}`
      )
      .then((response) => {
        const processedData = response?.data?.data || [];
        setData(Array.isArray(processedData) ? processedData : []);
      })
      .catch((error) => {
        console.error("Error fetching sponsorship packages:", error);
        setData([]);
      });
  }, [selectedConferenceId]);

  useEffect(() => {
    if (selectedConferenceId) {
      getData();
    }
  }, [selectedConferenceId, getData]);

  const reversedData = useMemo(() => [...data].reverse(), [data]);

  const standardRows = [
    { label: "Maximum Sponsors per category", key: "max_sponsors" },
    { label: "Booth Size", key: "booth_size" },
    { label: "Conference Booklet ad", key: "booklet_ad" },
    { label: "Website Advertisement", key: "website_ad" },
    { label: "Bags Inserts", key: "bags_inserts" },
    { label: "Logo on Backdrop (Main Hall)", key: "backdrop_logo" },
    { label: "Free Non-Residential Registration", key: "non_residential_reg" },
    { label: "Free Residential Registration", key: "residential_reg" },
  ];

  const groupedExtras = useMemo(() => {
    const extrasMap = new Map();

    reversedData.forEach((sponsorship) => {
      if (!Array.isArray(sponsorship?.extras)) return;

      sponsorship.extras.forEach((extra, extraIdx) => {
        const label = extra?.label?.trim();
        if (!label) return;

        if (!extrasMap.has(label)) {
          extrasMap.set(label, {
            label,
            valuesBySponsorshipId: {},
            order: `${sponsorship?.id || "sp"}-${extra?.id || extraIdx}`,
          });
        }

        const existingGroup = extrasMap.get(label);

        existingGroup.valuesBySponsorshipId[sponsorship.id] =
          extra?.value ?? "N/A";
      });
    });

    return Array.from(extrasMap.values());
  }, [reversedData]);

  const totalBodyRows = 1 + standardRows.length + groupedExtras.length;

  const styles = {
    wrapper: { marginTop: 20 },

    conferenceBar: {
      marginBottom: 14,
      padding: 12,
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      background: "#fff",
    },
    label: {
      display: "block",
      marginBottom: 6,
      fontSize: 13,
      fontWeight: 600,
      color: "#374151",
    },
    select: {
      width: "100%",
      maxWidth: 380,
      padding: "10px 40px 10px 12px",
      border: selectFocused ? "1px solid #60a5fa" : "1px solid #d1d5db",
      borderRadius: 8,
      backgroundColor: loadingConferences ? "#f9fafb" : "#fff",
      color: loadingConferences ? "#6b7280" : "#111827",
      fontSize: 14,
      backgroundImage:
        "linear-gradient(45deg, transparent 50%, #6b7280 50%), linear-gradient(135deg, #6b7280 50%, transparent 50%)",
      backgroundPosition: "calc(100% - 18px) 52%, calc(100% - 12px) 52%",
      backgroundSize: "6px 6px, 6px 6px",
      backgroundRepeat: "no-repeat",
      outline: selectFocused ? "2px solid rgba(59, 130, 246, 0.35)" : "none",
      outlineOffset: selectFocused ? 2 : 0,
      cursor: loadingConferences ? "not-allowed" : "pointer",
    },

    header: {
      margin: "12px 0",
      fontSize: 16,
      fontWeight: 700,
      color: "#111827",
    },

    tableWrap: {
      overflowX: "auto",
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      background: "#fff",
    },
    table: {
      width: "100%",
      minWidth: 900,
      borderCollapse: "separate",
      borderSpacing: 0,
    },

    th: {
      padding: "12px 14px",
      borderBottom: "1px solid #eef2f7",
      borderRight: "1px solid #eef2f7",
      verticalAlign: "top",
      fontSize: 13,
      color: "#374151",
      fontWeight: 700,
      background: "#f9fafb",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "12px 14px",
      borderBottom: "1px solid #eef2f7",
      borderRight: "1px solid #eef2f7",
      verticalAlign: "top",
      fontSize: 13,
      color: "#111827",
      whiteSpace: "nowrap",
      background: "#fff",
    },
    stickyCol: {
      position: "sticky",
      left: 0,
      zIndex: 1,
      background: "#fff",
      fontWeight: 600,
    },
    stickyHeadCol: {
      position: "sticky",
      left: 0,
      zIndex: 2,
      background: "#f9fafb",
      fontWeight: 700,
    },

    checkbox: {
      width: 18,
      height: 18,
      marginTop: 8,
      cursor: "pointer",
    },

    empty: {
      marginTop: 10,
      padding: 12,
      border: "1px dashed #d1d5db",
      borderRadius: 10,
      color: "#6b7280",
      background: "#fff",
    },
  };

  const renderCellStyle = (base, isLastCol) => ({
    ...base,
    borderRight: isLastCol ? "0" : base.borderRight,
  });

  const renderRowCellStyle = (base, isLastRow) => ({
    ...base,
    borderBottom: isLastRow ? "0" : base.borderBottom,
  });

  return (
    <div style={styles.wrapper}>
      <div style={styles.conferenceBar}>
        <label style={styles.label}>Select Conference</label>

        <select
          value={selectedConferenceId}
          onChange={(e) => {
            setSelectedConferenceId(e.target.value);
            setSelectedSponsorshipIds([]);
            onSelectedSponsorshipsChange([]);
          }}
          onFocus={() => setSelectFocused(true)}
          onBlur={() => setSelectFocused(false)}
          style={styles.select}
          disabled={loadingConferences}
        >
          <option value="">
            {loadingConferences ? "Loading conferences..." : "Select conference"}
          </option>

          {conferences.map((c) => {
            const id = c?.id || c?.conference_id || c?._id;
            const label =
              c?.name || c?.title || c?.conference_name || `Conference #${id}`;

            if (!id) return null;

            return (
              <option key={id} value={String(id)}>
                {label}
              </option>
            );
          })}
        </select>
      </div>

      {reversedData.length > 0 && (
        <div style={styles.header}>Sponsorship Packages</div>
      )}

      {reversedData.length > 0 && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.stickyHeadCol }}>Details</th>

                {reversedData.map((row, index) => {
                  const isLast = index === reversedData.length - 1;
                  return (
                    <th
                      key={row.id || index}
                      style={renderCellStyle(styles.th, isLast)}
                    >
                      {row.item || row.name || `Package ${index + 1}`}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              <tr>
                <td style={{ ...styles.td, ...styles.stickyCol }}>
                  Total Package Price
                </td>

                {reversedData.map((row, index) => {
                  const isLast = index === reversedData.length - 1;
                  const isLastRow = totalBodyRows === 1;

                  return (
                    <td
                      key={row.id || index}
                      style={renderCellStyle(
                        renderRowCellStyle(styles.td, isLastRow),
                        isLast
                      )}
                    >
                      {row.price || row.total_price || "N/A"}
                      <br />
                      <input
                        type="checkbox"
                        style={styles.checkbox}
                        checked={selectedSponsorshipIds?.includes(row.id)}
                        onChange={() => handleCheckboxChange(row.id)}
                      />
                    </td>
                  );
                })}
              </tr>

              {standardRows.map((r, rowIdx) => {
                const isLastRow =
                  rowIdx === standardRows.length - 1 && groupedExtras.length === 0;

                return (
                  <tr key={r.key}>
                    <td
                      style={renderRowCellStyle(
                        { ...styles.td, ...styles.stickyCol },
                        isLastRow
                      )}
                    >
                      {r.label}
                    </td>

                    {reversedData.map((row, index) => {
                      const isLastCol = index === reversedData.length - 1;
                      const cellBase = renderRowCellStyle(styles.td, isLastRow);

                      return (
                        <td
                          key={row.id || index}
                          style={renderCellStyle(cellBase, isLastCol)}
                        >
                          {row?.[r.key] ?? "N/A"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}

              {groupedExtras.map((extraGroup, extraIdx) => {
                const isLastRow = extraIdx === groupedExtras.length - 1;

                return (
                  <tr key={`extra-group-${extraGroup.label}-${extraIdx}`}>
                    <td
                      style={renderRowCellStyle(
                        { ...styles.td, ...styles.stickyCol },
                        isLastRow
                      )}
                    >
                      {extraGroup.label}
                    </td>

                    {reversedData.map((row, index) => {
                      const isLastCol = index === reversedData.length - 1;
                      const cellBase = renderRowCellStyle(styles.td, isLastRow);
                      const value =
                        extraGroup.valuesBySponsorshipId[row.id] ?? "";

                      return (
                        <td
                          key={row.id || index}
                          style={renderCellStyle(cellBase, isLastCol)}
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedConferenceId && reversedData.length === 0 && (
        <div style={styles.empty}>No sponsorship packages found.</div>
      )}
    </div>
  );
};

export default SponsorshipTable;